// components/workFlow/ParentRequestSelectionPopup.tsx
// Popup for selecting a parent gate pass request when creating a supplementary request
// NOTE: Adjust the import path below to match your project structure

import React, { useState, useEffect, useCallback } from 'react';
import { ParentRequest, ParentRequestSelectionPopupProps } from '../types/GatePassTypes';

export const ParentRequestSelectionPopup: React.FC<ParentRequestSelectionPopupProps> = ({
  isOpen,
  getAccessToken,
  onClose,
  onSelect,
  onStatusUpdate,
  currentSelection
}) => {
  const [requests, setRequests] = useState<ParentRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(currentSelection?.id || null);

  const fetchAcceptedRequests = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      // Use the same API_BASE_URL pattern as the rest of the app
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://gatepass-api-1753632520.azurewebsites.net/api';
      const url = `${API_BASE_URL}/gatepass_list?filterType=requester`;

      console.log('🔍 Parent Request Popup - Fetching URL:', url);

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('🔍 Parent Request Popup - Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Parent Request Popup - Error response:', errorText);
        throw new Error(`Failed to fetch requests (${response.status})`);
      }

      // Read as text first to debug JSON parse issues
      const responseText = await response.text();
      console.log('🔍 Parent Request Popup - Response preview:', responseText.substring(0, 200));

      let data: any;
      try {
        data = JSON.parse(responseText);
      } catch (parseErr) {
        console.error('❌ JSON parse failed. Raw response:', responseText.substring(0, 500));
        throw new Error('Invalid response from server - not valid JSON');
      }

      console.log('✅ Parent Request Popup - Parsed data:', { success: data.success, count: data.data?.length });

      if (data.success && data.data) {
        // Client-side filter: only show "Approved" requests with requestType = 'new'
        // These are the requests eligible to be parent of a supplementary request
        const acceptedRequests = data.data.filter(
          (req: any) =>
            req.status === 'Approved' && req.requestType === 'new'
        );
        setRequests(acceptedRequests);

        if (acceptedRequests.length === 0) {
          setError('No approved requests found that are eligible for supplementary submission.');
        }
      } else {
        setRequests([]);
        setError('No eligible requests found.');
      }
    } catch (err) {
      console.error('Error fetching accepted requests:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to load requests'
      );
    } finally {
      setLoading(false);
    }
  }, [getAccessToken]);

  // Fetch requests when popup opens
  useEffect(() => {
    if (isOpen) {
      fetchAcceptedRequests();
      setSelectedId(currentSelection?.id || null);
    }
  }, [isOpen, fetchAcceptedRequests, currentSelection]);

  // Filter requests by search term
  const filteredRequests = requests.filter((req) => {
    const term = searchTerm.toLowerCase();
    return (
      req.name.toLowerCase().includes(term) ||
      req.companyName.toLowerCase().includes(term) ||
      req.id.toString().includes(term) ||
      (req.contractNumber && req.contractNumber.toLowerCase().includes(term)) ||
      (req.passIdentificationName && req.passIdentificationName.toLowerCase().includes(term))
    );
  });

  const handleConfirm = () => {
    const selected = requests.find((r) => r.id === selectedId);
    if (selected) {
      onSelect(selected);
      onStatusUpdate({
        type: 'success',
        message: `Parent request #${selected.id} selected for supplementary request`
      });
      onClose();
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="popup-overlay" style={overlayStyle}>
      <div className="popup-container" style={containerStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <div>
            <h3 style={titleStyle}>Select Parent Request</h3>
            <p style={subtitleStyle}>
              Choose the original request for this supplementary submission
            </p>
          </div>
          <button onClick={onClose} style={closeButtonStyle} title="Close">
            ✕
          </button>
        </div>

        {/* Search */}
        <div style={searchContainerStyle}>
          <input
            type="text"
            placeholder="Search by name, company, ID, or contract..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={searchInputStyle}
          />
        </div>

        {/* Content */}
        <div style={contentStyle}>
          {loading ? (
            <div style={centeredMessageStyle}>
              <div style={spinnerStyle} />
              <p style={{ color: '#64748b', marginTop: '12px' }}>
                Loading accepted requests...
              </p>
            </div>
          ) : error && requests.length === 0 ? (
            <div style={centeredMessageStyle}>
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>📋</div>
              <p style={{ color: '#64748b', margin: '0 0 16px 0' }}>{error}</p>
              <button onClick={fetchAcceptedRequests} style={retryButtonStyle}>
                Retry
              </button>
            </div>
          ) : filteredRequests.length === 0 ? (
            <div style={centeredMessageStyle}>
              <p style={{ color: '#64748b' }}>
                No requests match your search.
              </p>
            </div>
          ) : (
            <div style={listStyle}>
              {filteredRequests.map((req) => (
                <div
                  key={req.id}
                  onClick={() => setSelectedId(req.id)}
                  style={{
                    ...requestCardStyle,
                    borderColor:
                      selectedId === req.id ? '#3b82f6' : '#e2e8f0',
                    backgroundColor:
                      selectedId === req.id ? '#eff6ff' : '#ffffff',
                    boxShadow:
                      selectedId === req.id
                        ? '0 0 0 2px rgba(59, 130, 246, 0.3)'
                        : 'none'
                  }}
                >
                  {/* Selection indicator */}
                  <div style={radioContainerStyle}>
                    <div
                      style={{
                        ...radioOuterStyle,
                        borderColor:
                          selectedId === req.id ? '#3b82f6' : '#cbd5e1'
                      }}
                    >
                      {selectedId === req.id && (
                        <div style={radioInnerStyle} />
                      )}
                    </div>
                  </div>

                  {/* Request details */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={cardHeaderStyle}>
                      <span style={requestIdBadgeStyle}>#{req.id}</span>
                      <span style={statusBadgeStyle}>
                        Approved
                      </span>
                    </div>

                    <div style={cardTitleStyle}>{req.name}</div>
                    <div style={cardCompanyStyle}>{req.companyName}</div>

                    <div style={cardMetaRowStyle}>
                      {req.contractNumber && (
                        <span style={metaItemStyle}>
                          📄 Contract: {req.contractNumber}
                        </span>
                      )}
                      {req.passIdentificationName && (
                        <span style={metaItemStyle}>
                          🏷️ {req.passIdentificationName}
                        </span>
                      )}
                      <span style={metaItemStyle}>
                        📅 Submitted: {formatDate(req.submittedAt)}
                      </span>
                    </div>

                    {req.startDate && req.endDate && (
                      <div style={dateRangeStyle}>
                        {formatDate(req.startDate)} → {formatDate(req.endDate)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          <span style={countStyle}>
            {filteredRequests.length} request{filteredRequests.length !== 1 ? 's' : ''} available
          </span>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button onClick={onClose} style={cancelButtonStyle}>
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!selectedId}
              style={{
                ...confirmButtonStyle,
                opacity: selectedId ? 1 : 0.5,
                cursor: selectedId ? 'pointer' : 'not-allowed'
              }}
            >
              Select Request
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Inline Styles ────────────────────────────────────────────────

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '20px'
};

const containerStyle: React.CSSProperties = {
  backgroundColor: '#ffffff',
  borderRadius: '16px',
  width: '100%',
  maxWidth: '680px',
  maxHeight: '80vh',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  overflow: 'hidden'
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  padding: '24px 24px 16px',
  borderBottom: '1px solid #f1f5f9'
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '18px',
  fontWeight: 600,
  color: '#1e293b'
};

const subtitleStyle: React.CSSProperties = {
  margin: '4px 0 0',
  fontSize: '13px',
  color: '#64748b'
};

const closeButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  fontSize: '18px',
  color: '#94a3b8',
  cursor: 'pointer',
  padding: '4px 8px',
  borderRadius: '6px',
  lineHeight: 1
};

const searchContainerStyle: React.CSSProperties = {
  padding: '12px 24px'
};

const searchInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 14px',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  fontSize: '14px',
  outline: 'none',
  boxSizing: 'border-box',
  color: '#1e293b'
};

const contentStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: '0 24px 16px',
  minHeight: '200px'
};

const centeredMessageStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '40px 20px',
  textAlign: 'center'
};

const spinnerStyle: React.CSSProperties = {
  width: '32px',
  height: '32px',
  border: '3px solid #e5e7eb',
  borderTop: '3px solid #3b82f6',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite'
};

const retryButtonStyle: React.CSSProperties = {
  padding: '8px 16px',
  backgroundColor: '#3b82f6',
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: 500
};

const listStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px'
};

const requestCardStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '14px',
  padding: '16px',
  border: '1.5px solid #e2e8f0',
  borderRadius: '12px',
  cursor: 'pointer',
  transition: 'all 0.15s ease'
};

const radioContainerStyle: React.CSSProperties = {
  paddingTop: '2px',
  flexShrink: 0
};

const radioOuterStyle: React.CSSProperties = {
  width: '20px',
  height: '20px',
  borderRadius: '50%',
  border: '2px solid #cbd5e1',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'border-color 0.15s ease'
};

const radioInnerStyle: React.CSSProperties = {
  width: '10px',
  height: '10px',
  borderRadius: '50%',
  backgroundColor: '#3b82f6'
};

const cardHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '6px'
};

const requestIdBadgeStyle: React.CSSProperties = {
  fontSize: '12px',
  fontWeight: 600,
  color: '#3b82f6',
  backgroundColor: '#eff6ff',
  padding: '2px 8px',
  borderRadius: '4px'
};

const statusBadgeStyle: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 500,
  color: '#059669',
  backgroundColor: '#ecfdf5',
  padding: '2px 8px',
  borderRadius: '4px'
};

const cardTitleStyle: React.CSSProperties = {
  fontSize: '15px',
  fontWeight: 600,
  color: '#1e293b',
  marginBottom: '2px'
};

const cardCompanyStyle: React.CSSProperties = {
  fontSize: '13px',
  color: '#64748b',
  marginBottom: '8px'
};

const cardMetaRowStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '12px',
  fontSize: '12px',
  color: '#94a3b8'
};

const metaItemStyle: React.CSSProperties = {
  whiteSpace: 'nowrap'
};

const dateRangeStyle: React.CSSProperties = {
  fontSize: '12px',
  color: '#64748b',
  marginTop: '6px',
  fontStyle: 'italic'
};

const footerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 24px',
  borderTop: '1px solid #f1f5f9',
  backgroundColor: '#fafbfc'
};

const countStyle: React.CSSProperties = {
  fontSize: '13px',
  color: '#94a3b8'
};

const cancelButtonStyle: React.CSSProperties = {
  padding: '10px 20px',
  backgroundColor: '#f1f5f9',
  color: '#475569',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: 500
};

const confirmButtonStyle: React.CSSProperties = {
  padding: '10px 20px',
  backgroundColor: '#3b82f6',
  color: '#ffffff',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: 500
};